import React, { ReactNode, useEffect, useState } from 'react';

import { formatDateString } from '~libs/dayjs';

import { Button, ErrorResponse, Spinner } from '~components';
import { Select } from '~components/form-fields/inputs';
import Modal from '~components/Modal';
import { TableView } from '~components/table-view-v3';

import { ClientInvoiceStatusTransition } from '~lookups/models';

import { modalColumns } from '~support-at-home/forms/co-contributions/modalList';

import { useGetClientInvoiceStatusTransitionsQuery } from '~lookups/actions/clientInvoiceStatuses';
import { useGetClientInvoiceQuery, useUpdateClientInvoiceMutation } from '~support-at-home/actions/clientInvoices';

import CoContributionExportActions from './CoContributionExportActions';

interface CoContributionsModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  clientId: string | null;
}

const SummaryRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="summary-row">
    <strong>{label}:</strong>
    <span>{value}</span>
  </div>
);

const CoContributionsModal: React.FC<CoContributionsModalProps> = ({ isOpen, setIsOpen, clientId }) => {
  const { data, isLoading, error: getError } = useGetClientInvoiceQuery({ id: clientId ?? '' }, { skip: !clientId });
  const { data: transitions = [] } = useGetClientInvoiceStatusTransitionsQuery();
  const [updateInvoice, { isLoading: isUpdating, error: updateError }] = useUpdateClientInvoiceMutation();

  const { client, items = [], invoice_date, due_date, reference = 'N/A', status_id, status, total_amount } = data ?? {};
  const { full_name, send_to } = client ?? {};

  const [statusId, setStatusId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setStatusId(null);
    }
  }, [isOpen]);

  const getStatusTransitionOptions = () => {
    const mappedTransitions = transitions
      .filter((transition: ClientInvoiceStatusTransition) => transition.from_status_id === status_id)
      .map((transition: ClientInvoiceStatusTransition) => ({
        label: transition.to_status?.name,
        value: transition.to_status_id,
      }))
      .filter((obj, i, self) => self.findIndex((t) => t.value === obj.value) === i);

    return [{ label: status?.name, value: status_id } as { label: string; value: string }, ...mappedTransitions];
  };

  const handleSave = async () => {
    if (!clientId || !statusId) return;
    const { error } = await updateInvoice({ id: clientId, status_id: statusId });
    if (!error) setIsOpen(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      setIsOpen={setIsOpen}
      header={`Edit Co-Contribution Invoice - ${reference}`}
      headerClassName="header"
      size="xl"
      footer={
        <div className="d-flex justify-content-between align-items-center w-100">
          <CoContributionExportActions row={clientId ? { id: clientId } : undefined} />
          <div className="d-flex gap-2">
            <Button color="success" disabled={!statusId || isUpdating} onClick={handleSave}>
              {isUpdating ? <Spinner loading size={5} /> : 'Save'}
            </Button>
            <Button color="secondary" disabled={isUpdating} onClick={() => setIsOpen(false)}>
              Close
            </Button>
          </div>
        </div>
      }
      footerClassName="d-flex"
    >
      <ErrorResponse message={getError?.message || updateError?.message} />
      {isLoading && (
        <div>
          <Spinner loading />
        </div>
      )}
      {data && (
        <div className="cocontributions-modal">
          {/* Summary Section */}
          <div className="summary">
            <div>
              <div className="mb-1">
                <strong>{full_name}</strong>
              </div>
              <div className="client-address">{send_to}</div>
            </div>
            <div className="details">
              <SummaryRow label="Reference" value={reference ?? 'N/A'} />
              <SummaryRow label="Invoice Date" value={invoice_date ? formatDateString(invoice_date) : 'N/A'} />
              <SummaryRow label="Due Date" value={due_date ? formatDateString(due_date) : 'N/A'} />
              <SummaryRow
                label="Status"
                value={
                  <Select
                    id="status-select"
                    name="status-select"
                    type="select"
                    value={statusId ?? status_id}
                    onChange={(_, val) => setStatusId(val as string)}
                    gateOptions={{ getValidOptions: getStatusTransitionOptions }}
                    className="select select-sm"
                    clearable={false}
                  />
                }
              />
            </div>
          </div>

          {/* Invoice Items Table */}
          <TableView id="co-contributions-modal-table" columns={modalColumns} data={items} readonly striped />

          {/* Invoice Total */}
          <div className="totals">
            <div className="totals-container">
              <hr className="my-2" />
              <div className="amount-row">
                <strong>Invoice Total:</strong>
                <span>${total_amount?.toFixed(2) ?? '0.00'}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default CoContributionsModal;
